import React, { useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface Particle {
  angle: number;
  radius: number;
  speed: number;
}

export function AccretionDisk() {
  const particlesRef = useRef<Particle[]>([]);
  
  // X-Ray flare state tracker
  const flareRef = useRef({
    x: 0,
    y: 0,
    radius: 0,
    maxRadius: 25,
    alpha: 0,
    duration: 0,
    active: false,
    nextTriggerTime: 1.0, // trigger first one after 1s
  });

  // Simple RGB color interpolation helper
  const getDiskColor = (temp: number) => {
    let r = 0, g = 0, b = 0;
    if (temp > 0.7) {
      // White to pale yellow
      const t = (temp - 0.7) / 0.3;
      r = Math.round(253 + (255 - 253) * t);
      g = Math.round(230 + (255 - 230) * t);
      b = Math.round(138 + (255 - 138) * t);
    } else if (temp > 0.3) {
      // Yellow to orange
      const t = (temp - 0.3) / 0.4;
      r = Math.round(249 + (253 - 249) * t);
      g = Math.round(115 + (230 - 115) * t);
      b = Math.round(22 + (138 - 22) * t);
    } else {
      // Orange to deep red
      const t = temp / 0.3;
      r = Math.round(127 + (249 - 127) * t);
      g = Math.round(29 + (115 - 29) * t);
      b = Math.round(29 + (22 - 29) * t);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;
    const cx = width / 2;
    const cy = height / 2;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 1. INITIALIZE PARTICLES IF NEEDED
    if (particlesRef.current.length === 0) {
      for (let i = 0; i < 80; i++) {
        particlesRef.current.push({
          angle: Math.random() * Math.PI * 2,
          radius: 30 + Math.random() * 90,
          speed: 0.5 + Math.random() * 1.0, // angular velocity base
        });
      }
    }

    // 2. UPDATE PARTICLES
    particlesRef.current.forEach((p) => {
      // Angular velocity increases as the particle gets closer (Keplerian effect)
      const angularSpeed = (p.speed + (60 / p.radius) * 1.5) * 1.5;
      p.angle += angularSpeed * deltaTime;
      
      // Radial drift inward
      p.radius -= 18 * deltaTime * (80 / p.radius);

      // Recycle if crossed event horizon threshold
      if (p.radius < 28) {
        p.radius = 100 + Math.random() * 20;
        p.angle = Math.random() * Math.PI * 2;
      }
    });

    // 3. UPDATE X-RAY FLARE
    const flare = flareRef.current;
    if (flare.active) {
      flare.duration += deltaTime;
      if (flare.duration >= 0.5) {
        flare.active = false;
        flare.nextTriggerTime = totalTime + 1.0 + Math.random() * 2.0; // 1-3 seconds wait
      } else {
        const t = flare.duration / 0.5;
        flare.radius = 4 + t * flare.maxRadius;
        flare.alpha = 1 - t;
      }
    } else if (totalTime > flare.nextTriggerTime) {
      flare.active = true;
      flare.duration = 0;
      flare.maxRadius = 15 + Math.random() * 15;
      
      // Choose a random location in the inner disk region
      const theta = Math.random() * Math.PI * 2;
      const R = 35 + Math.random() * 30;
      flare.x = cx + R * Math.cos(theta);
      flare.y = cy + R * Math.sin(theta) * 0.35;
    }

    // Helper to draw a single ring segment or overlay
    const drawRingHalf = (r: number, color: string, opacity: number, isTop: boolean) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(1, 0.35); // 30 degree perspective tilt
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = color;

      if (isTop) {
        // Draw full top half at base opacity
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(0, 0, r, Math.PI, 2 * Math.PI);
        ctx.stroke();

        // Overlay Doppler boost on top-left quadrant (representing approaching gas)
        ctx.globalAlpha = opacity * 0.5; // Stacking makes it 1.5x alpha
        ctx.beginPath();
        ctx.arc(0, 0, r, Math.PI, 1.5 * Math.PI);
        ctx.stroke();
      } else {
        // Draw full bottom half at base opacity
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI);
        ctx.stroke();

        // Overlay Doppler boost on bottom-left quadrant
        ctx.globalAlpha = opacity * 0.5; // Stacking makes it 1.5x alpha
        ctx.beginPath();
        ctx.arc(0, 0, r, 0.5 * Math.PI, Math.PI);
        ctx.stroke();
      }
      ctx.restore();
    };

    // Helper to draw a particle
    const drawParticle = (p: Particle) => {
      const px = cx + p.radius * Math.cos(p.angle);
      const py = cy + p.radius * Math.sin(p.angle) * 0.35;

      const temp = 1 - (p.radius - 30) / 90;
      ctx.fillStyle = getDiskColor(temp);

      // Doppler brightening for particles: left side is brighter
      const isLeft = Math.cos(p.angle) < 0;
      ctx.globalAlpha = isLeft ? 0.9 : 0.4;

      ctx.beginPath();
      ctx.arc(px, py, 1.5, 0, Math.PI * 2);
      ctx.fill();
    };

    // Helper to draw the flare
    const drawFlare = () => {
      if (!flare.active) return;
      ctx.save();
      const flareGrad = ctx.createRadialGradient(
        flare.x, flare.y, 0,
        flare.x, flare.y, flare.radius
      );
      flareGrad.addColorStop(0, `rgba(255, 255, 255, ${flare.alpha})`);
      flareGrad.addColorStop(0.3, `rgba(147, 197, 253, ${flare.alpha * 0.8})`); // light blue
      flareGrad.addColorStop(1, 'rgba(59, 130, 246, 0)');
      
      ctx.fillStyle = flareGrad;
      ctx.beginPath();
      ctx.arc(flare.x, flare.y, flare.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    // =========================================================================
    // PAINTER'S ALGORITHM DRAW SEQUENCE
    // =========================================================================

    // A. DRAW BACK SIDE (TOP HALF) OF ACCRETION DISK RINGS
    // Stack outer -> inner (120 to 30) to overlay brighter core on top
    for (let r = 120; r >= 30; r -= 3) {
      const temp = 1 - (r - 30) / 90;
      const color = getDiskColor(temp);
      drawRingHalf(r, color, 0.15, true);
    }

    // B. DRAW BACK SIDE PARTICLES (sin(angle) < 0)
    particlesRef.current.forEach((p) => {
      if (Math.sin(p.angle) < 0) {
        drawParticle(p);
      }
    });

    // C. DRAW BACK SIDE FLARE (if flare is above y-center)
    if (flare.active && flare.y < cy) {
      drawFlare();
    }

    // D. DRAW EVENT HORIZON SILHOUETTE
    ctx.fillStyle = '#000000';
    ctx.globalAlpha = 1.0;
    ctx.beginPath();
    ctx.arc(cx, cy, 28, 0, Math.PI * 2);
    ctx.fill();

    // E. DRAW FRONT SIDE (BOTTOM HALF) OF ACCRETION DISK RINGS
    for (let r = 120; r >= 30; r -= 3) {
      const temp = 1 - (r - 30) / 90;
      const color = getDiskColor(temp);
      drawRingHalf(r, color, 0.15, false);
    }

    // F. DRAW FRONT SIDE PARTICLES (sin(angle) >= 0)
    particlesRef.current.forEach((p) => {
      if (Math.sin(p.angle) >= 0) {
        drawParticle(p);
      }
    });

    // G. DRAW FRONT SIDE FLARE (if flare is on/below y-center)
    if (flare.active && flare.y >= cy) {
      drawFlare();
    }

    // H. DRAW SOFT OUTER GLOW OVER THE ENTIRE DISK
    ctx.save();
    const glowGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 150);
    glowGrad.addColorStop(0, 'rgba(249, 115, 22, 0.22)');
    glowGrad.addColorStop(0.5, 'rgba(249, 115, 22, 0.08)');
    glowGrad.addColorStop(1, 'rgba(249, 115, 22, 0)');
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, 150, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Reset alpha
    ctx.globalAlpha = 1.0;

    // =========================================================================
    // LABELS & ANNOTATIONS
    // =========================================================================
    
    // Top-Left Headers
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Accretion Disk', 16, 26);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Superheated gas and dust spiraling into the black hole', 16, 42);

    // Event Horizon pointer text and arrow
    // Label starts at (cx + 90, cy - 60)
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Event Horizon', cx + 90, cy - 58);
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '9px sans-serif';
    ctx.fillText('Boundary of no escape', cx + 90, cy - 46);

    // Draw pointing arrow line from label start towards EH edge
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx + 85, cy - 54);
    ctx.lineTo(cx + 20, cy - 18);
    ctx.stroke();

    // Arrowhead at (cx + 20, cy - 18)
    const angle = Math.atan2(-18 - (-54), 20 - 85);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.beginPath();
    ctx.moveTo(cx + 20, cy - 18);
    ctx.lineTo(
      cx + 20 - 5 * Math.cos(angle - Math.PI / 6),
      cy - 18 - 5 * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      cx + 20 - 5 * Math.cos(angle + Math.PI / 6),
      cy - 18 - 5 * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fill();

  }, []);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
